// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {useCallback, useRef, useState} from 'react'
import type * as z from 'zod'

import {t} from '../../../../lib/utils/localization'

/**
 * Localized last-resort message for a required field that is empty and has no other validation.
 * Contentful fields normally carry a `REQUIRED` validation that errors first, so this only covers
 * base `useForm` consumers that mark a field `required` without attaching validations.
 */
const DEFAULT_REQUIRED_MESSAGE = t('This field is required.')

type HtmlFormControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

const getElementValue = (el: HtmlFormControl): string | boolean => {
  if (el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
    return el.value
  }

  const {type, checked, value} = el

  if (type !== 'checkbox') {
    return value
  }

  const hasExplicitValue = el.getAttribute('value') !== null

  if (hasExplicitValue) {
    return checked ? value : ''
  }

  return checked
}

export type FormField = {
  id: string
  label?: string
  name: string
  required?: boolean
  value: string | boolean
  validations?: Array<{
    message: string
    schema: z.ZodType
  }>
}

export type OnSubmit = (data: Record<string, string | boolean | string[] | undefined>) => Promise<unknown>

export type RegisterOptions = {
  label?: FormField['label']
  required?: FormField['required']
  validations?: FormField['validations']
}

export type RegisterReturn<FormControl extends HtmlFormControl> = {
  id: string
  name: string
  onChange: React.ChangeEventHandler<FormControl>
  ref: React.RefCallback<FormControl>
}

export type UseFormReturn = ReturnType<typeof useForm>

type FormSnapshot = {
  errors: Record<string, string>
  formFields: Record<string, FormField>
  touched: boolean
}

const INITIAL_FORM_SNAPSHOT: FormSnapshot = {
  errors: {},
  formFields: {},
  touched: false,
}

const removeKey = <Value>(record: Record<string, Value>, key: string) => {
  if (!(key in record)) {
    return record
  }

  const nextRecord = {...record}
  delete nextRecord[key]
  return nextRecord
}

export const useForm = () => {
  /**
   * Event handlers need synchronous access to the latest form data, while render-time
   * consumers need an immutable snapshot. Both references always point to the same snapshot.
   */
  const formSnapshotRef = useRef(INITIAL_FORM_SNAPSHOT)
  // Keep callbacks stable so publishing a snapshot does not reattach refs and undo an explicit unregister.
  const registrationsRef = useRef<Record<string, RegisterReturn<HtmlFormControl>>>({})
  const [formSnapshot, setFormSnapshot] = useState(INITIAL_FORM_SNAPSHOT)

  const updateFormSnapshot = useCallback((update: (current: FormSnapshot) => FormSnapshot) => {
    const currentSnapshot = formSnapshotRef.current
    const nextSnapshot = update(currentSnapshot)

    if (nextSnapshot === currentSnapshot) {
      return
    }

    formSnapshotRef.current = nextSnapshot
    setFormSnapshot(nextSnapshot)
  }, [])

  const unregister = useCallback(
    (name: string) => {
      updateFormSnapshot(currentSnapshot => {
        const formFields = removeKey(currentSnapshot.formFields, name)
        const errors = removeKey(currentSnapshot.errors, name)

        if (formFields === currentSnapshot.formFields && errors === currentSnapshot.errors) {
          return currentSnapshot
        }

        return {
          ...currentSnapshot,
          errors,
          formFields,
        }
      })
    },
    [updateFormSnapshot],
  )

  /**
   * This function allows consumers of this hook to register inputs for the form. Once
   * an input is registered, the useForm hook will automatically track its value, perform
   * validations, and pass it down to the submit function.
   *
   * @param name The HTML name of the field to register
   * @param registerOpts Additional options applied when the field's ref attaches
   * @returns A collection of properties that can be passed down to the associated input (e.g., onChange)
   */
  const register = useCallback(
    <FormControl extends HtmlFormControl>(
      name: string,
      registerOpts?: RegisterOptions,
    ): RegisterReturn<FormControl> => {
      let registration = registrationsRef.current[name]

      if (registration === undefined) {
        const id = `form-field-${name}`

        registration = {
          id,
          name,
          onChange: event => {
            const value = getElementValue(event.target)

            updateFormSnapshot(currentSnapshot => {
              const formField = currentSnapshot.formFields[name]

              if (formField === undefined) {
                return currentSnapshot
              }

              return {
                ...currentSnapshot,
                formFields: {
                  ...currentSnapshot.formFields,
                  [name]: {...formField, value},
                },
                touched: true,
              }
            })
          },
          ref: element => {
            if (element === null) {
              if (registrationsRef.current[name] === registration) {
                delete registrationsRef.current[name]
              }

              unregister(name)
              return
            }

            const formField: FormField = {
              id,
              label: registerOpts?.label,
              name,
              required: registerOpts?.required,
              value: getElementValue(element),
              validations: registerOpts?.validations,
            }

            updateFormSnapshot(currentSnapshot => ({
              ...currentSnapshot,
              formFields: {
                ...currentSnapshot.formFields,
                [name]: formField,
              },
            }))
          },
        }

        registrationsRef.current[name] = registration
      }

      return registration
    },
    [unregister, updateFormSnapshot],
  )

  /**
   * Use this function to ensure the form is correct before submitting the data. It will
   * loop through the collection of fields and check if they’re valid. Once verified, it
   * will pass the collection of values to the function you want to use for the actual submission.
   *
   * @param onSubmit A function to call when the form is going to be submitted
   * @returns An event handler for the form submit event
   */
  const handleSubmit = (onSubmit: OnSubmit) => {
    return async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      let nextErrors: Record<string, string> = {}

      for (const entry of Object.entries(formSnapshotRef.current.formFields)) {
        const [name, {value, validations, required}] = entry

        // If this form field is empty and non-required, we jump to the next one.
        if (typeof value === 'string' && value === '' && !required) {
          continue
        }

        for (const validation of validations ?? []) {
          const {success: isValid} = validation.schema.safeParse(value)

          if (isValid) {
            // This validation passed, so we can move to the next one.
            continue
          }

          nextErrors = {
            ...nextErrors,

            [name]: validation.message,
          }

          // We encountered an error, so we can stop checking the other validations.
          break
        }

        if (nextErrors[name]) {
          // We already have an error for this field, so we can move to the next one.
          continue
        }

        /**
         * Finally, if the field is required and empty, and no other validation errors were found,
         * we add a default error message.
         *
         * This is a last resort since, for example, form fields coming from Contentful will usually have
         * a custom `REQUIRED` validation that will be triggered first.
         */
        if (required && (value === '' || value === false)) {
          nextErrors = {
            ...nextErrors,

            [name]: DEFAULT_REQUIRED_MESSAGE,
          }
        }
      }

      const formWithErrors = Object.keys(nextErrors).length > 0

      updateFormSnapshot(currentSnapshot => ({
        ...currentSnapshot,
        errors: nextErrors,
      }))

      if (formWithErrors) {
        return
      }

      // Start with registered form fields
      const data = Object.entries(formSnapshotRef.current.formFields).reduce<
        Record<string, string | boolean | string[]>
      >((acc, [name, formField]) => {
        acc[name] = formField.value

        return acc
      }, {})

      // Also collect native form data to capture unregistered fields (like checkbox arrays)
      const formData = new FormData(event.target as HTMLFormElement)

      // Get unique field names upfront to avoid duplicate processing
      const uniqueKeys = new Set(formData.keys())

      // Process FormData to handle checkbox arrays (fields with [] suffix)
      for (const key of uniqueKeys) {
        if (key.endsWith('[]')) {
          // Remove the [] suffix for the data key
          const fieldName = key.slice(0, -2)
          // Collect all values for this field name as an array
          const allValues = formData.getAll(key) as string[]
          data[fieldName] = allValues
        }
        // Note: We only collect checkbox arrays (with [] suffix) from FormData
        // Regular fields should be handled by the registration system
      }

      await onSubmit(data)
    }
  }

  return {
    /**
     * This is a representation of the current errors in the form. It uses the input
     * names as keys and the error messages as values. Consumers of this hook can use
     * this object to check for errors and render the appropriate validation messages.
     */
    errors: formSnapshot.errors,

    /**
     * This is a representation of the current registered form fields.
     */
    formFields: formSnapshot.formFields,

    formState: {
      errors: Object.keys(formSnapshot.errors).length > 0,
      touched: formSnapshot.touched,
    },
    handleSubmit,
    register,
    unregister,
  }
}
